import { useDeveloper } from "@shared/ui/developer";
import { useEffect, type ComponentType, type ReactNode } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@shared/ui/dropdown-menu";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "@shared/ui/context-menu";
import { IconCheck, IconChevronRight } from "@shared/ui/icons";

/**
 * Меню по правой кнопке.
 *
 * Одно на все места, где встречается один и тот же объект: человек в сетке,
 * тот же человек в полосе и он же в списке участников открывают одно меню с
 * одними пунктами. Разные меню на один объект — это когда «заглушить» есть в
 * сетке и нет в полосе, и человек начинает искать, где же оно было.
 *
 * Чего нет по праву — того нет вовсе, а не серым: серый пункт обещает
 * действие и отвечает отказом. Чего нет в продукте — помечено планом, чтобы
 * не выглядело сломанным.
 */
export interface MenuItem {
  label: string;
  /** Правая подпись: горячая клавиша, текущее значение, слово «право». */
  hint?: string;
  kind?: "danger" | "plan";
  /**
   * Выбранный пункт из нескольких взаимоисключающих — с галочкой.
   *
   * Место под галочку держат все пункты такого набора, а не только
   * отмеченный: иначе выбор другого уровня сдвигает подписи, и глаз ловит
   * движение вместо смысла.
   */
  on?: boolean;
  /** Пункт из набора с выбором: держит место под галочку. */
  choice?: boolean;
  /**
   * Переключатель — как «Скрыть заглушённые каналы» в Discord: квадрат справа,
   * отмечен по `on`. Меню после щелчка не закрывается — видно, что щёлкнуло.
   */
  toggle?: boolean;
  /**
   * Один уровень из нескольких — кружком справа, как уровни уведомлений в
   * колокольчике Discord; отмечен по `on`. Меню, как у переключателя, не
   * закрывается: видно, куда перешёл выбор.
   */
  radio?: boolean;
  /** Значок слева от подписи — как в меню сервера Discord. */
  icon?: ComponentType<{ size?: number }>;
  /**
   * Вторая строка под подписью, приглушённая, — как имя устройства под
   * «Устройством вывода» в Discord. Длинная переносится, а не режется:
   * имена вроде «Наушники (2- Temple) (040b:0897)» иначе не прочесть.
   */
  note?: string;
  /**
   * Вставка вместо пункта: ползунок громкости между пунктами, как в Discord.
   * Сама не нажимается, подсветка через неё не ездит; `label` нужен только
   * как ключ.
   */
  block?: ReactNode;
  /**
   * Вложенное меню: пункт не действие, а выбор из списка.
   *
   * Раньше это делалось подменой — первое меню закрывалось, второе
   * открывалось в той же точке. Путь назад при этом терялся: закрыв второе,
   * человек оказывался нигде, а не там, откуда пришёл.
   *
   * Пункт со вложенным меню сам не нажимается — `onPick` у него не бывает.
   */
  items?: MenuItem[];
  onPick?: () => void;
}

/** Разделитель между разделами меню. */
export const SEPARATOR: MenuItem = { label: "—" };

/**
 * Пункты меню — общая начинка обеих оболочек.
 *
 * Компоненты приходят набором, а не берутся здесь: у `DropdownMenu` и
 * `ContextMenu` пункт, разделитель и подпись — разные компоненты Radix с
 * разными типами, и общего предка у них нет. Передать набор дешевле, чем
 * держать две копии этой разметки и править их порознь.
 */
/**
 * Части Radix, из которых собрано меню.
 *
 * Приходят набором, а не берутся здесь: у `DropdownMenu` и `ContextMenu` пункт,
 * разделитель и подпись — разные компоненты с разными типами, и общего предка
 * у них нет. Передать набор дешевле, чем держать две копии этой разметки.
 */
interface Parts {
  Label: typeof DropdownMenuLabel | typeof ContextMenuLabel;
  Separator: typeof DropdownMenuSeparator | typeof ContextMenuSeparator;
  Item: typeof DropdownMenuItem | typeof ContextMenuItem;
  Shortcut: typeof DropdownMenuShortcut | typeof ContextMenuShortcut;
  Sub: typeof DropdownMenuSub | typeof ContextMenuSub;
  SubTrigger: typeof DropdownMenuSubTrigger | typeof ContextMenuSubTrigger;
  SubContent: typeof DropdownMenuSubContent | typeof ContextMenuSubContent;
}

/** Начинка пункта: галочка, подпись, метка плана. */
function Inside({ item }: { item: MenuItem }) {
  const Icon = item.icon;

  return (
    <span className="inline-flex min-w-0 flex-1 items-center gap-[7px] whitespace-nowrap">
      {(item.choice || item.on) && !item.toggle && !item.radio && (
        <span
          className={`inline-flex w-[14px] shrink-0 justify-center text-bright ${
            item.on ? "" : "invisible"
          }`}
        >
          <IconCheck size={12} />
        </span>
      )}
      {Icon && (
        <span className="menu-icon">
          <Icon size={17} />
        </span>
      )}
      {/* Длинная подпись режется многоточием, как в Discord, а не раздвигает меню. */}
      {item.note ? (
        <span className="flex min-w-0 flex-col">
          <span className="truncate">{item.label}</span>
          <span className="menu-note">{item.note}</span>
        </span>
      ) : (
        <span className="min-w-0 truncate">{item.label}</span>
      )}
      {item.kind === "plan" && <span className="plan-tag">план</span>}
    </span>
  );
}

/** Один пункт: обычный или открывающий вложенное меню. */
function Row({ parts, item }: { parts: Parts; item: MenuItem }) {
  const { Item, Shortcut, Sub, SubTrigger, SubContent, Separator } = parts;
  // Со второй строкой пункт растёт по содержимому, а не держит свои 36 точек.
  const tall = item.note ? "h-auto min-h-[36px] py-[7px]" : undefined;

  if (item.block) return <>{item.block}</>;

  if (item.items) {
    return (
      <Sub>
        <SubTrigger disabled={item.kind === "plan"} className={tall}>
          <Inside item={item} />
          {item.hint && <Shortcut>{item.hint}</Shortcut>}
          {/* Стрелка — как в Discord: пункт не действие, а вход во вложенное меню. */}
          <span className="menu-sub-arrow" aria-hidden>
            <IconChevronRight size={15} />
          </span>
        </SubTrigger>
        <SubContent>
          {item.items.map((inner, index) =>
            inner.label === "—" ? (
              <Separator key={`sep-${index}`} />
            ) : (
              <Row key={inner.label} parts={parts} item={inner} />
            ),
          )}
        </SubContent>
      </Sub>
    );
  }

  return (
    <Item
      className={tall}
      variant={item.kind === "danger" ? "destructive" : "default"}
      // Планируемое не нажимается: кнопка, которая ничего не делает,
      // читается как сломанная.
      disabled={item.kind === "plan" || !item.onPick}
      onSelect={(event) => {
        if (item.toggle || item.radio) event.preventDefault();
        item.onPick?.();
      }}
    >
      <Inside item={item} />
      {item.hint && <Shortcut>{item.hint}</Shortcut>}
      {item.toggle && (
        <span className={`menu-box${item.on ? " on" : ""}`} aria-hidden>
          {item.on && <IconCheck size={12} strokeWidth={2.5} />}
        </span>
      )}
      {item.radio && <span className={`menu-radio${item.on ? " on" : ""}`} aria-hidden />}
    </Item>
  );
}

function Body({
  parts,
  title,
  sub,
  items,
  children,
}: {
  parts: Parts;
  /** Без заголовка меню начинается сразу с пунктов — как меню устройств Discord. */
  title?: string;
  sub?: string;
  items: MenuItem[];
  children?: React.ReactNode;
}) {
  const { Label, Separator } = parts;

  return (
    <>
      {/* С подписью ниже имени метка в две строки: в одну они слипались. */}
      {title && (
        <Label className={sub ? "h-auto flex-col items-start pt-[8px]" : undefined}>
          {title}
          {sub && <div className="mt-[3px] text-[11.5px] tracking-normal text-muted-foreground">{sub}</div>}
        </Label>
      )}

      {children}

      {(title || children) && <Separator />}

      {items.map((item, index) =>
        item.label === "—" ? (
          <Separator key={`sep-${index}`} />
        ) : (
          <Row key={item.label} parts={parts} item={item} />
        ),
      )}
    </>
  );
}

/**
 * Меню, открытое нажатием на кнопку.
 *
 * Точка приходит извне, потому что открывает его не опора, а щелчок по кнопке
 * «…» или «+»: Radix отсчитывает меню от элемента, а отсчитывать не от чего.
 * Точка им и становится — пустая метка нулевого размера ровно там, куда ткнули.
 *
 * Правой кнопкой это меню больше не открывают: для неё есть `MenuOn` ниже, и
 * она умеет то, чего здесь не было, — долгое нажатие на касании.
 */
export function Menu({
  title,
  sub,
  at,
  items,
  children,
  onClose,
}: {
  title?: string;
  sub?: string;
  /** Куда ткнули — угол меню. */
  at: { x: number; y: number };
  items: MenuItem[];
  /** Верхний блок над пунктами. */
  children?: React.ReactNode;
  onClose: () => void;
}) {
  /*
   * Прокрутка уводит меню от того, по чему щёлкнули: висеть посреди другого
   * содержимого оно не должно. Radix при изменении окна умеет переставить
   * меню на новое место, но переставлять его некуда — точка щелчка к тому
   * времени уже не значит того, что значила.
   */
  useEffect(() => {
    window.addEventListener("resize", onClose);
    return () => window.removeEventListener("resize", onClose);
  }, [onClose]);

  return (
    <DropdownMenu open onOpenChange={(open) => !open && onClose()} modal={false}>
      <DropdownMenuTrigger asChild>
        <span
          aria-hidden
          style={{ position: "fixed", left: at.x, top: at.y, width: 0, height: 0 }}
        />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="bottom"
        align="start"
        /* Меню у края экрана откидывается, а не уезжает за него. */
        collisionPadding={8}
        /*
         * Фокус после закрытия остаётся там, где был. Возвращать его на
         * метку нулевого размера некуда, а прежнее меню фокусом не
         * распоряжалось вовсе.
         */
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <Body
          parts={{
            Label: DropdownMenuLabel,
            Separator: DropdownMenuSeparator,
            Item: DropdownMenuItem,
            Shortcut: DropdownMenuShortcut,
            Sub: DropdownMenuSub,
            SubTrigger: DropdownMenuSubTrigger,
            SubContent: DropdownMenuSubContent,
          }}
          title={title}
          sub={sub}
          items={items}
        >
          {children}
        </Body>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * То же меню, но по правой кнопке — обёрткой вокруг того, на чём его зовут.
 *
 * Управление здесь вывернуто по сравнению с `Menu`: не экран решает, какое
 * меню показать, а сама строка носит своё. Иначе никак — Radix ловит
 * `contextmenu` на опоре, и опора должна быть внутри.
 *
 * Взамен уходит всё, что подделка делала руками: `preventDefault`, съём
 * координат, состояние «по чему ткнули», метка нулевого размера и слушатель
 * `resize`. И приходит долгое нажатие: на касании меню открывается тоже.
 */
export function MenuOn({
  title,
  sub,
  items,
  head,
  children,
  disabled = false,
  menuClassName,
  ...trigger
}: Omit<React.HTMLAttributes<HTMLElement>, "title" | "children"> & {
  /*
   * Остальное — опоре. Так меню встаёт внутрь другой обёртки с `asChild`
   * (подсказки `Hint`): та передаёт свои обработчики и ref сюда, а отсюда
   * они доезжают до самой кнопки.
   */
  ref?: React.Ref<HTMLElement>;
  title: string;
  sub?: string;
  items: MenuItem[];
  /**
   * Верхний блок меню — у меню человека это громкость.
   *
   * Отдельным свойством, а не `children`: здесь `children` уже занято тем, по
   * чему щёлкают, и второго смысла у него быть не может.
   */
  head?: React.ReactNode;
  /** То, по чему щёлкают правой кнопкой. */
  children: React.ReactNode;
  /** Меню не открывается вовсе: прав нет или объект не наш. */
  disabled?: boolean;
  /** Класс самого меню — например, свой слой поверх просмотрщика картинок. */
  menuClassName?: string;
}) {
  /*
   * Выключенное меню остаётся в разметке, выключается только опора: иначе
   * смена признака пересоздаёт всё, что внутри, — строка ленты на время
   * правки теряла бы свои картинки и поле.
   */
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild disabled={disabled} {...trigger}>
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent
        className={menuClassName}
        collisionPadding={8}
        /*
         * Пункт сам увёл фокус — в поле правки, в поле ответа — и назад, на то,
         * что было в фокусе до меню, его не возвращают. Остался он на
         * исчезнувшем пункте — возвращают как обычно.
         */
        onCloseAutoFocus={(event) => {
          if (document.activeElement && document.activeElement !== document.body) event.preventDefault();
        }}
      >
        <Body
          parts={{
            Label: ContextMenuLabel,
            Separator: ContextMenuSeparator,
            Item: ContextMenuItem,
            Shortcut: ContextMenuShortcut,
            Sub: ContextMenuSub,
            SubTrigger: ContextMenuSubTrigger,
            SubContent: ContextMenuSubContent,
          }}
          title={title}
          sub={sub}
          items={items}
        >
          {head}
        </Body>
      </ContextMenuContent>
    </ContextMenu>
  );
}

/**
 * «Копировать ID» в конце меню — только в режиме разработчика, как в Discord.
 * Состояние берётся в момент сборки меню: меню собирают при открытии.
 */
export function withId(items: MenuItem[], id: string, label = "Копировать ID"): MenuItem[] {
  if (!useDeveloper.getState().on) return items;
  return [
    ...items,
    ...(items.length ? [SEPARATOR] : []),
    { label, onPick: () => void navigator.clipboard?.writeText(id).catch(() => {}) },
  ];
}
